/**
 * Screen sharing to the presentation wall: a desktop participant captures a tab or window
 * with getDisplayMedia and the video track rides the voice peer connections; everyone else
 * sees it on the 5 m wall display (a VideoTexture) and can pull a personal clone close to
 * read. Audio from the share, when the browser gives it, plays flat through the voice mesh.
 */
import * as THREE from 'three';
import type { Net } from '../app/net.ts';
import type { VoiceMesh } from '../audio/VoiceMesh.ts';
import { PALETTE, font, wrapText } from '../dashboards/charts.ts';
import { HALL, PRESENTATION } from '../world/plan.ts';

/** The wall display on the entrance wall west of the door, 5 x 2.8 m, facing the hall. */
export const WALL = {
  x: PRESENTATION.x,
  y: PRESENTATION.y,
  z: HALL.front - 0.14,
  w: PRESENTATION.width,
  h: PRESENTATION.height,
  yaw: Math.PI,
};
const CLONE = { w: 1.6, h: 0.9, distance: 1.3 };

export interface ScreenShareHandlers {
  notice(lines: string[], ms?: number): void;
  inXr(): boolean;
  name(): string;
}

export class ScreenShare {
  readonly group = new THREE.Group();
  readonly wall: THREE.Mesh;
  readonly clone: THREE.Mesh;
  private readonly video = document.createElement('video');
  private readonly idleCanvas = document.createElement('canvas');
  private readonly idleTexture: THREE.CanvasTexture;
  private readonly wallMaterial: THREE.MeshBasicMaterial;
  private readonly cloneMaterial: THREE.MeshBasicMaterial;
  private videoTexture: THREE.VideoTexture | undefined;
  private localStream: MediaStream | undefined;
  private remoteStream: MediaStream | undefined;
  /** Who the room says is sharing. */
  sharer: { sessionId: string; name: string } | undefined;
  lastError: string | undefined;

  constructor(
    private readonly net: Net,
    private readonly voice: VoiceMesh,
    private readonly handlers: ScreenShareHandlers,
  ) {
    this.group.name = 'screen-share';
    this.video.autoplay = true;
    this.video.muted = true;
    this.video.playsInline = true;
    this.video.setAttribute('playsinline', '');
    this.video.style.display = 'none';
    document.body.append(this.video);
    this.idleCanvas.width = 1024;
    this.idleCanvas.height = 576;
    this.idleTexture = new THREE.CanvasTexture(this.idleCanvas);
    this.idleTexture.colorSpace = THREE.SRGBColorSpace;
    this.drawIdle();
    this.wallMaterial = new THREE.MeshBasicMaterial({ map: this.idleTexture, toneMapped: false });
    this.wall = new THREE.Mesh(new THREE.PlaneGeometry(WALL.w, WALL.h), this.wallMaterial);
    this.wall.name = 'presentation-wall';
    this.wall.position.set(WALL.x, WALL.y, WALL.z);
    this.wall.rotation.y = WALL.yaw;
    this.cloneMaterial = new THREE.MeshBasicMaterial({ map: this.idleTexture, toneMapped: false });
    this.clone = new THREE.Mesh(new THREE.PlaneGeometry(CLONE.w, CLONE.h), this.cloneMaterial);
    this.clone.name = 'screen-clone';
    this.clone.visible = false;
    this.clone.renderOrder = 40;
    this.group.add(this.wall, this.clone);
  }

  /** True while this client sends its screen. */
  get sharing(): boolean {
    return !!this.localStream;
  }

  /** True while a remote share is playing on the wall. */
  get receiving(): boolean {
    return !!this.remoteStream && this.remoteStream.active;
  }

  get wallHasVideo(): boolean {
    return this.wallMaterial.map === this.videoTexture && !!this.videoTexture;
  }

  get cloneVisible(): boolean {
    return this.clone.visible;
  }

  /** Starts sharing: the given stream (dev hooks) or a getDisplayMedia prompt. Desktop only. */
  async start(stream?: MediaStream): Promise<boolean> {
    if (this.localStream) return true;
    if (this.handlers.inXr() && !stream) {
      this.handlers.notice([
        'Screen share is desktop only',
        'Share from a laptop; the wall shows it here.',
      ]);
      return false;
    }
    if (this.sharer && this.sharer.sessionId !== this.net.sessionId) {
      this.handlers.notice(['Not now', `${this.sharer.name} is already sharing`]);
      return false;
    }
    let s = stream;
    if (!s) {
      try {
        s = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
      } catch (err) {
        this.lastError = (err as Error).message;
        this.handlers.notice(['Screen share cancelled', this.lastError], 3000);
        return false;
      }
    }
    this.localStream = s;
    const track = s.getVideoTracks()[0];
    track?.addEventListener('ended', () => this.stop());
    this.voice.setShareStream(s);
    this.net.shareState(true);
    this.show(s);
    this.handlers.notice(
      ['Sharing your screen', 'It is on the presentation wall for everyone.'],
      3000,
    );
    return true;
  }

  stop(): void {
    const s = this.localStream;
    if (!s) return;
    this.localStream = undefined;
    this.voice.setShareStream(undefined);
    for (const t of s.getTracks()) t.stop();
    this.net.shareState(false);
    if (!this.remoteStream) this.clearWall();
    this.handlers.notice(['Screen share ended'], 2000);
  }

  /** From the voice mesh: a remote member's share started or ended. */
  onRemote(id: string, stream: MediaStream | undefined): void {
    if (stream) {
      this.remoteStream = stream;
      this.show(stream);
    } else if (this.remoteStream && (!this.sharer || this.sharer.sessionId === id)) {
      this.remoteStream = undefined;
      if (this.localStream) this.show(this.localStream);
      else this.clearWall();
    }
  }

  /** From the room: who is sharing. */
  onShareState(sessionId: string, name: string, on: boolean): void {
    if (on) {
      this.sharer = { sessionId, name };
      if (sessionId !== this.net.sessionId)
        this.handlers.notice([`${name} is sharing a screen`, 'On the presentation wall.'], 3000);
    } else if (this.sharer?.sessionId === sessionId) {
      this.sharer = undefined;
      if (!this.localStream) {
        this.remoteStream = undefined;
        this.clearWall();
      }
    }
    this.drawIdle();
  }

  private show(stream: MediaStream): void {
    this.video.srcObject = stream;
    void this.video.play().catch(() => {});
    if (!this.videoTexture) {
      this.videoTexture = new THREE.VideoTexture(this.video);
      this.videoTexture.colorSpace = THREE.SRGBColorSpace;
    }
    this.wallMaterial.map = this.videoTexture;
    this.wallMaterial.needsUpdate = true;
    this.cloneMaterial.map = this.videoTexture;
    this.cloneMaterial.needsUpdate = true;
  }

  private clearWall(): void {
    this.video.srcObject = null;
    this.wallMaterial.map = this.idleTexture;
    this.wallMaterial.needsUpdate = true;
    this.cloneMaterial.map = this.idleTexture;
    this.cloneMaterial.needsUpdate = true;
    this.clone.visible = false;
  }

  /** A personal clone of the wall 1.3 m in front of the viewer; again to put it away. */
  togglePull(head: THREE.Vector3, forward: THREE.Vector3): boolean {
    if (this.clone.visible) {
      this.clone.visible = false;
      return false;
    }
    const dir = forward.clone().setY(0).normalize();
    this.clone.position.copy(head).addScaledVector(dir, CLONE.distance);
    this.clone.position.y = Math.max(1.0, head.y - 0.1);
    this.clone.rotation.set(0, Math.atan2(dir.x, dir.z) + Math.PI, 0);
    this.clone.visible = true;
    return true;
  }

  /** Spotlight: the wall stays bright over the dimmed room. */
  setSpotlight(on: boolean): void {
    for (const m of [this.wallMaterial, this.cloneMaterial]) {
      m.depthTest = !on;
      m.transparent = on;
      m.needsUpdate = true;
    }
    this.wall.renderOrder = on ? 590 : 0;
    this.clone.renderOrder = on ? 595 : 40;
  }

  /**
   * The wall while nobody shares: a title and the two ways to put something on it. Every
   * line wraps inside the frame and the type shrinks until the block fits, so no text is
   * ever cut at the edges (it was, with one fixed-size line per sentence).
   */
  private drawIdle(): void {
    const ctx = this.idleCanvas.getContext('2d')!;
    const W = this.idleCanvas.width;
    const H = this.idleCanvas.height;
    ctx.fillStyle = PALETTE.bg;
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = PALETTE.selected;
    ctx.lineWidth = 2;
    ctx.strokeRect(24, 24, W - 48, H - 48);
    const maxW = W - 160;
    const maxH = H - 140;
    const paragraphs = this.sharer
      ? [`${this.sharer.name} is sharing a screen, waiting for the video…`]
      : [
          'Share a screen from a desktop (wrist menu: Share), or present slides from the lectern.',
          'On the Quest, the wrist menu\'s "Pull screen" brings a copy of this wall close to you.',
        ];
    // Find the largest body size at which the title and the wrapped paragraphs fit.
    let body = 24;
    let layout: { lines: string[][]; titleSize: number; height: number } | undefined;
    for (; body >= 12; body -= 1) {
      const titleSize = Math.round(body * 1.55);
      ctx.font = font(body, 500);
      const lines = paragraphs.map((para) => wrapText(ctx, para, maxW));
      const lineH = Math.round(body * 1.3 * 1.45);
      const height =
        titleSize * 1.3 * 1.4 +
        lines.reduce((sum, l) => sum + l.length * lineH, 0) +
        (lines.length - 1) * lineH * 0.5;
      const widest = Math.max(...lines.flat().map((l) => ctx.measureText(l).width));
      ctx.font = font(titleSize, 700, true);
      const titleW = ctx.measureText('PRESENTATION WALL').width;
      if (height <= maxH && widest <= maxW && titleW <= maxW) {
        layout = { lines, titleSize, height };
        break;
      }
    }
    if (!layout) {
      ctx.font = font(12, 500);
      layout = {
        lines: paragraphs.map((para) => wrapText(ctx, para, maxW)),
        titleSize: 19,
        height: maxH,
      };
    }
    const lineH = Math.round(body * 1.3 * 1.45);
    let y = (H - layout.height) / 2 + layout.titleSize * 1.3 * 0.7;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = PALETTE.teal;
    ctx.font = font(layout.titleSize, 700, true);
    ctx.fillText('PRESENTATION WALL', W / 2, y);
    y += layout.titleSize * 1.3 * 0.7 + lineH;
    ctx.fillStyle = PALETTE.muted;
    ctx.font = font(body, 500);
    layout.lines.forEach((para, i) => {
      for (const line of para) {
        ctx.fillText(line, W / 2, y);
        y += lineH;
      }
      if (i < layout!.lines.length - 1) y += lineH * 0.5;
    });
    this.idleTexture.needsUpdate = true;
  }

  dispose(): void {
    this.stop();
    this.video.srcObject = null;
    this.video.remove();
    this.videoTexture?.dispose();
    this.idleTexture.dispose();
    this.wallMaterial.dispose();
    this.cloneMaterial.dispose();
    this.wall.geometry.dispose();
    this.clone.geometry.dispose();
  }
}
